import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { PageContainer, Section } from '../components/AdminContentWrapper';
import { DataContext } from '../../../context/DataContext';
import VerifiedQueryService from '../../../services/VerifiedQueryService';
import SemanticModelService from '../../../services/SemanticModelService';
import VerifiedQueryFormDialog from '../components/VerifiedQueryFormDialog';
import TablePaginationBar from '../../../components/common/TablePaginationBar';
import { useTablePagination } from '../../../hooks';

// Tope de la consulta que alimenta el badge de "Pendientes de revisión".
// El backend pagina con cursor y no puede dar un total sin recorrer todo,
// así que por encima de este número el badge muestra "100+".
const PENDING_COUNT_CAP = 100;

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short' }).format(d);
};

const truncate = (text, max = 90) => {
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

const STATUS_META = {
  ACTIVE: { label: 'Activa', color: 'var(--wb-success)', bg: 'var(--wb-x-bg-4ade8033, rgba(74, 222, 128, 0.2))' },
  DISABLED: { label: 'Deshabilitada', color: 'var(--wb-warning)', bg: 'var(--wb-x-bg-fbbf242e, rgba(251, 191, 36, 0.18))' },
  PENDING_REVIEW: { label: 'Pendiente', color: 'var(--wb-accent)', bg: 'var(--wb-accent-18)' },
  REJECTED: { label: 'Rechazada', color: 'var(--wb-danger)', bg: 'var(--wb-x-bg-f8717133, rgba(248, 113, 113, 0.2))' }
};

const StatusChip = ({ status }) => {
  const meta = STATUS_META[status] || { label: status || '—', color: 'var(--wb-text-label)', bg: 'var(--wb-border-16)' };
  return <Chip size="small" label={meta.label} sx={{ color: meta.color, backgroundColor: meta.bg, fontWeight: 600 }} />;
};

/**
 * "Biblioteca" es el CRUD manual (Fases 4-5) -- se excluyen acá los
 * PENDING_REVIEW, que viven en la pestaña "Pendientes de revisión"
 * (Fase 8, candidatos capturados desde el 👍 del chat). REJECTED se sigue
 * mostrando en la biblioteca como registro histórico de qué se rechazó.
 */
const VerifiedQueriesOverview = () => {
  const { hasPermission, isPlatformRoot, isTenantAdmin, userData } = useContext(DataContext);
  const canManage = Boolean(
    isPlatformRoot || isTenantAdmin || (typeof hasPermission === 'function' && hasPermission('agents.manage'))
  );
  const accessChecked = Boolean(userData);

  const [view, setView] = useState('library'); // 'library' | 'review'
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingCountIsCapped, setPendingCountIsCapped] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [models, setModels] = useState([]);
  const [modelFilter, setModelFilter] = useState('');
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [dialogState, setDialogState] = useState({ open: false, item: null });

  const modelNameById = useMemo(() => {
    const map = new Map();
    models.forEach((m) => map.set(m.semantic_model_id, m.name || m.semantic_model_id));
    return map;
  }, [models]);

  // La pestaña "Todas" descarta los candidatos en JS (el backend filtra
  // por status con igualdad, no por "distinto de"). Eso puede achicar una
  // página; con pocos candidatos por tenant es aceptable, y el cursor de
  // abajo trae la siguiente tanda igual.
  const filtrosDeLaVista = useCallback((data) => (
    view === 'review' ? data.items : data.items.filter((i) => i.status !== 'PENDING_REVIEW')
  ), [view]);

  const argumentosDeLaVista = useCallback(() => (
    view === 'review'
      ? { status: 'PENDING_REVIEW' }
      : { semanticModelId: modelFilter || undefined }
  ), [view, modelFilter]);

  const fetchList = useCallback(async () => {
    if (!canManage) return;
    setListLoading(true);
    setListError('');
    try {
      const data = await VerifiedQueryService.list(argumentosDeLaVista());
      setItems(filtrosDeLaVista(data));
      setCursor(data.nextCursor);
    } catch (error) {
      setListError(error?.message || 'No se pudo obtener el listado de verified queries.');
    } finally {
      setListLoading(false);
    }
  }, [canManage, argumentosDeLaVista, filtrosDeLaVista]);

  const fetchMore = useCallback(async () => {
    if (!canManage || !cursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const data = await VerifiedQueryService.list({ ...argumentosDeLaVista(), cursor });
      setItems((prev) => [...prev, ...filtrosDeLaVista(data)]);
      setCursor(data.nextCursor);
    } catch (error) {
      setListError(error?.message || 'No se pudieron obtener más verified queries.');
    } finally {
      setIsLoadingMore(false);
    }
  }, [canManage, cursor, isLoadingMore, argumentosDeLaVista, filtrosDeLaVista]);

  // `view` y `modelFilter` cambian el conjunto listado: la página 3 del
  // resultado anterior no significa nada en el nuevo.
  const { pageRows, paginationProps } = useTablePagination(items, {
    storageKey: 'verifiedQueries',
    resetDeps: [view, modelFilter],
    hasMore: Boolean(cursor),
    isLoadingMore,
    onNeedMore: fetchMore
  });

  const refreshPendingCount = useCallback(async () => {
    if (!canManage) return;
    try {
      // Se pide una tanda grande solo para el badge: si el backend deja
      // cursor es que hay más de PENDING_COUNT_CAP, y se muestra "99+".
      const data = await VerifiedQueryService.list({
        status: 'PENDING_REVIEW', limit: PENDING_COUNT_CAP
      });
      setPendingCount(data.items.length);
      setPendingCountIsCapped(Boolean(data.nextCursor));
    } catch (error) {
      console.error('VerifiedQueriesOverview.refreshPendingCount', error);
    }
  }, [canManage]);

  const fetchModels = useCallback(async () => {
    if (!canManage) return;
    try {
      const res = await SemanticModelService.list();
      const list = Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];
      setModels(list.filter((m) => !m?.is_deleted));
    } catch (error) {
      console.error('VerifiedQueriesOverview.fetchModels', error);
    }
  }, [canManage]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    fetchModels();
    refreshPendingCount();
  }, [fetchModels, refreshPendingCount]);

  const handleDelete = async (queryItem) => {
    if (!window.confirm(`¿Eliminar la verified query "${truncate(queryItem.question, 60)}"?`)) return;
    setBusyId(queryItem.query_id);
    setFeedback(null);
    try {
      await VerifiedQueryService.remove(queryItem.query_id);
      setFeedback({ severity: 'success', message: 'Verified query eliminada.' });
      await fetchList();
      await refreshPendingCount();
    } catch (error) {
      setFeedback({ severity: 'error', message: error?.message || 'No se pudo eliminar.' });
    } finally {
      setBusyId(null);
    }
  };

  const handleReviewDecision = async (queryItem, decision) => {
    setBusyId(queryItem.query_id);
    setFeedback(null);
    try {
      await VerifiedQueryService.update(queryItem.query_id, { status: decision });
      setFeedback({
        severity: 'success',
        message: decision === 'ACTIVE' ? 'Candidato aprobado -- ya está disponible para el agente.' : 'Candidato rechazado.'
      });
      await fetchList();
      await refreshPendingCount();
    } catch (error) {
      setFeedback({ severity: 'error', message: error?.message || 'No se pudo actualizar el candidato.' });
    } finally {
      setBusyId(null);
    }
  };

  const handleSaved = async () => {
    setDialogState({ open: false, item: null });
    setFeedback({ severity: 'success', message: 'Verified query guardada.' });
    await fetchList();
    await refreshPendingCount();
  };

  if (!accessChecked) {
    return (
      <PageContainer title="Consultas verificadas" description="Cargando acceso...">
        <Section title="Cargando acceso">
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <Typography sx={{ color: 'var(--wb-text-label)' }}>Verificando permisos...</Typography>
          </Stack>
        </Section>
      </PageContainer>
    );
  }

  if (!canManage) {
    return (
      <PageContainer title="Consultas verificadas" description="Solo quien administra agentes puede gestionar el repositorio de verified queries.">
        <Section title="Acceso restringido">
          <Alert severity="warning" sx={{ backgroundColor: 'var(--wb-warning-12)', color: 'var(--wb-warning)' }}>
            Necesitas el permiso <strong>agents.manage</strong> (o ser administrador) para acceder a esta sección.
          </Alert>
        </Section>
      </PageContainer>
    );
  }

  const isReview = view === 'review';

  return (
    <PageContainer
      title="Consultas verificadas"
      description="Pares pregunta → SQL verificados, que el agente usa como referencia para reducir alucinaciones de columnas, joins o lógica de negocio."
      actions={[
        <Button
          key="refresh"
          variant="outlined"
          color="info"
          startIcon={<RefreshIcon />}
          onClick={() => { fetchList(); refreshPendingCount(); }}
          disabled={listLoading}
          sx={{ textTransform: 'none', borderColor: 'var(--wb-accent-40)', color: 'var(--wb-accent)' }}
        >
          Actualizar
        </Button>,
        <Button
          key="new"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogState({ open: true, item: null })}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Nueva verified query
        </Button>
      ]}
    >
      {feedback && (
        <Box sx={{ mb: 3 }}>
          <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ backgroundColor: 'var(--wb-x-bg-0e749026, rgba(14, 116, 144, 0.15))', color: 'var(--wb-text)' }}>
            {feedback.message}
          </Alert>
        </Box>
      )}

      <Tabs
        value={view}
        onChange={(_e, newValue) => setView(newValue)}
        sx={{
          mb: 2,
          minHeight: 0,
          '& .MuiTab-root': { textTransform: 'none', color: 'var(--wb-muted-80)', minHeight: 0, py: 1 },
          '& .Mui-selected': { color: 'var(--wb-accent) !important' },
          '& .MuiTabs-indicator': { backgroundColor: 'var(--wb-accent)' }
        }}
      >
        <Tab value="library" label="Biblioteca" />
        <Tab
          value="review"
          label={
            <Badge
              color="info"
              badgeContent={pendingCountIsCapped ? `${pendingCount}+` : pendingCount}
              sx={{ '& .MuiBadge-badge': { right: -14, backgroundColor: 'var(--wb-accent)', color: 'var(--wb-bg-page)', fontWeight: 700 } }}
            >
              <Box sx={{ pr: pendingCount > 0 ? 1.5 : 0 }}>Pendientes de revisión</Box>
            </Badge>
          }
        />
      </Tabs>

      <Section
        title={isReview ? 'Candidatos pendientes de aprobación' : 'Verified queries'}
        subtitle={
          isReview
            ? `${items.length} candidato${items.length === 1 ? '' : 's'} capturado${items.length === 1 ? '' : 's'} desde el feedback de usuarios en el chat.`
            : `${items.length} verified quer${items.length === 1 ? 'y' : 'ies'}${modelFilter ? ' · filtrado por modelo' : ''}.`
        }
      >
        {!isReview && (
          <Box sx={{ mb: 2, maxWidth: 320 }}>
            <FormControl fullWidth sx={{ '& .MuiInputBase-root': { backgroundColor: 'var(--wb-bg-surface)', color: 'var(--wb-text-title)' } }}>
              <InputLabel>Filtrar por modelo semántico</InputLabel>
              <Select
                label="Filtrar por modelo semántico"
                value={modelFilter}
                onChange={(e) => setModelFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>Todas (cualquier modelo + globales)</em>
                </MenuItem>
                {models.map((m) => (
                  <MenuItem key={m.semantic_model_id} value={m.semantic_model_id}>
                    {m.name || m.semantic_model_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {listError && (
          <Alert severity="error" sx={{ mb: 2, backgroundColor: 'var(--wb-danger-18)', color: 'var(--wb-danger-soft)' }}>
            {listError}
          </Alert>
        )}

        <Box sx={{ position: 'relative', overflowX: 'auto' }}>
          <Table
            size="small"
            sx={{
              minWidth: 900,
              '& th': { color: 'var(--wb-x-text-94a3b8e6, rgba(148, 163, 184, 0.9))', fontWeight: 600, borderBottomColor: 'var(--wb-border)' },
              '& td': { color: 'var(--wb-text)', borderBottomColor: 'var(--wb-border-06)' }
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Pregunta</TableCell>
                {isReview && <TableCell>SQL capturado</TableCell>}
                <TableCell>Modelo semántico</TableCell>
                {!isReview && <TableCell>Estado</TableCell>}
                <TableCell>{isReview ? 'Capturado' : 'Actualizado'}</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && !listLoading && (
                <TableRow>
                  <TableCell colSpan={isReview ? 5 : 5}>
                    <Typography sx={{ color: 'var(--wb-muted-70)', textAlign: 'center', py: 3 }}>
                      {isReview
                        ? 'No hay candidatos pendientes de revisión.'
                        : `Todavía no hay verified queries${modelFilter ? ' para este modelo' : ''}.`}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {pageRows.map((vq) => (
                <TableRow key={vq.query_id}>
                  <TableCell>
                    {truncate(vq.question)}
                    {vq.description && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'var(--wb-muted-70)' }}>
                        {truncate(vq.description, 80)}
                      </Typography>
                    )}
                  </TableCell>
                  {isReview && (
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', color: 'var(--wb-x-text-e2e8f0d9, rgba(226, 232, 240, 0.85))' }}
                      >
                        {truncate(vq.sql_query, 120)}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    {vq.semantic_model_id ? (modelNameById.get(vq.semantic_model_id) || vq.semantic_model_id) : (
                      <Chip size="small" label="Global" sx={{ backgroundColor: 'var(--wb-border-16)', color: 'var(--wb-text-label)' }} />
                    )}
                  </TableCell>
                  {!isReview && (
                    <TableCell>
                      <StatusChip status={vq.status} />
                    </TableCell>
                  )}
                  <TableCell>{formatDateTime(isReview ? vq.created_at : vq.updated_at)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {isReview && (
                        <>
                          <Tooltip title="Aprobar (queda ACTIVA y disponible para el agente)">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleReviewDecision(vq, 'ACTIVE')}
                                disabled={busyId === vq.query_id}
                                sx={{ color: 'var(--wb-success)' }}
                              >
                                <CheckCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Rechazar">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleReviewDecision(vq, 'REJECTED')}
                                disabled={busyId === vq.query_id}
                                sx={{ color: 'var(--wb-danger)' }}
                              >
                                <HighlightOffIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Editar">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => setDialogState({ open: true, item: vq })}
                            sx={{ color: 'var(--wb-accent)' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(vq)}
                            disabled={busyId === vq.query_id}
                            sx={{ color: 'var(--wb-danger)' }}
                          >
                            {busyId === vq.query_id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {items.length > 0 && <TablePaginationBar {...paginationProps} />}

          {listLoading && (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="center"
              sx={{ position: 'absolute', inset: 0, backgroundColor: 'var(--wb-bg-overlay-70)' }}
            >
              <CircularProgress size={24} />
              <Typography sx={{ color: 'var(--wb-text-label)' }}>Actualizando...</Typography>
            </Stack>
          )}
        </Box>
      </Section>

      <VerifiedQueryFormDialog
        open={dialogState.open}
        item={dialogState.item}
        models={models}
        onClose={() => setDialogState({ open: false, item: null })}
        onSaved={handleSaved}
      />
    </PageContainer>
  );
};

export default VerifiedQueriesOverview;
